import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "../../components/common/Table";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import JobStatusBadge from "../../components/Jobs/JobStatusBadge";
import JobDetailsModal from "./JobDetailsModal";
import { api } from "../../services/api";
import {
  PlayArrow,
  Pause,
  Refresh,
  Visibility,
} from "@mui/icons-material";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const Actions = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  align-items: center;
`;

const TableCard = styled(Card)`
  overflow-x: auto;
`;

const JobTypeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    if (props.type === "scheduled_notification")
      return "#3b82f6";
    if (props.type === "reminder") return "#10b981";
    return "#6b7280";
  }};
  color: white;
`;

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString();
};

const formatCronPattern = (pattern) => {
  if (!pattern) return "N/A";
  // Convert cron pattern to readable format if needed
  return pattern;
};

const JobsList = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailsModal, setShowDetailsModal] =
    useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobs();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getJobs();
      setJobs(response.results || []);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Xəta baş verdi"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (job) => {
    try {
      const details = await api.getJobDetails(job.name);
      setSelectedJob(details);
      setShowDetailsModal(true);
    } catch (err) {
      console.error("Error fetching job details:", err);
      alert("Job detallarını yükləmək mümkün olmadı");
    }
  };

  const handleStartJob = async (jobName) => {
    if (
      !window.confirm(
        `"${jobName}" job-unu indi işə salmaq istəyirsiniz?`
      )
    ) {
      return;
    }
    try {
      await api.startJob({ name: jobName });
      alert("Job uğurla işə salındı");
      fetchJobs();
    } catch (err) {
      console.error("Error starting job:", err);
      alert("Job-u işə salmaq mümkün olmadı");
    }
  };

  const handlePauseJob = async (jobName) => {
    if (
      !window.confirm(
        `"${jobName}" job-unu dayandırmaq istəyirsiniz?`
      )
    ) {
      return;
    }
    try {
      await api.pauseJob({ name: jobName });
      alert("Job uğurla dayandırıldı");
      fetchJobs();
    } catch (err) {
      console.error("Error pausing job:", err);
      alert("Job-u dayandırmaq mümkün olmadı");
    }
  };

  const handleResumeJob = async (jobName) => {
    try {
      await api.resumeJob({ name: jobName });
      alert("Job uğurla davam etdirildi");
      fetchJobs();
    } catch (err) {
      console.error("Error resuming job:", err);
      alert("Job-u davam etdirmək mümkün olmadı");
    }
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>Jobs</Title>
          <Actions>
            <Button onClick={fetchJobs} theme={theme}>
              <Refresh /> Yenilə
            </Button>
          </Actions>
        </PageHeader>

        <TableCard theme={theme}>
          <Table theme={theme}>
            <TableHead theme={theme}>
              <TableRow theme={theme}>
                <TableHeader theme={theme}>
                  Job Name
                </TableHeader>
                <TableHeader theme={theme}>
                  Type
                </TableHeader>
                <TableHeader theme={theme}>
                  Status
                </TableHeader>
                <TableHeader theme={theme}>
                  Schedule
                </TableHeader>
                <TableHeader theme={theme}>
                  Next Run
                </TableHeader>
                <TableHeader theme={theme}>
                  Last Run
                </TableHeader>
                <TableHeader theme={theme}>
                  Last Status
                </TableHeader>
                <TableHeader theme={theme}>
                  Actions
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={8} theme={theme}>
                    Yüklənir...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow theme={theme}>
                  <TableCell
                    colSpan={8}
                    theme={theme}
                    style={{ color: "#ef4444" }}
                  >
                    Xəta: {error}
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow theme={theme}>
                  <TableCell colSpan={8} theme={theme}>
                    Job tapılmadı
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow
                    key={job.id || job.name}
                    theme={theme}
                  >
                    <TableCell theme={theme}>
                      <strong>{job.name}</strong>
                    </TableCell>
                    <TableCell theme={theme}>
                      <JobTypeBadge
                        type={job.type || "other"}
                      >
                        {job.type ===
                        "scheduled_notification"
                          ? "Scheduled Notification"
                          : job.type === "reminder"
                          ? "Reminder"
                          : "Other"}
                      </JobTypeBadge>
                    </TableCell>
                    <TableCell theme={theme}>
                      <JobStatusBadge status={job.status} />
                    </TableCell>
                    <TableCell theme={theme}>
                      {formatCronPattern(
                        job.repeatInterval
                      )}
                    </TableCell>
                    <TableCell theme={theme}>
                      {formatDate(job.nextRunAt)}
                    </TableCell>
                    <TableCell theme={theme}>
                      {formatDate(job.lastRunAt)}
                    </TableCell>
                    <TableCell theme={theme}>
                      {job.failCount > 0 ? (
                        <span style={{ color: "#ef4444" }}>
                          Failed ({job.failCount})
                        </span>
                      ) : job.lastRunAt ? (
                        <span style={{ color: "#10b981" }}>
                          Success
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell theme={theme}>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleViewDetails(job)
                          }
                          theme={theme}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                          }}
                        >
                          <Visibility fontSize="small" />{" "}
                          Detallar
                        </Button>
                        {job.status === "paused" ? (
                          <Button
                            variant="outline"
                            onClick={() =>
                              handleResumeJob(job.name)
                            }
                            theme={theme}
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px",
                            }}
                          >
                            <PlayArrow fontSize="small" />{" "}
                            Davam et
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() =>
                              handlePauseJob(job.name)
                            }
                            theme={theme}
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px",
                            }}
                          >
                            <Pause fontSize="small" />{" "}
                            Dayandır
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleStartJob(job.name)
                          }
                          theme={theme}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                          }}
                        >
                          <PlayArrow fontSize="small" />{" "}
                          İndi işə sal
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>

        {showDetailsModal && selectedJob && (
          <JobDetailsModal
            job={selectedJob}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedJob(null);
            }}
            onRefresh={fetchJobs}
          />
        )}
      </PageContainer>
    </MainLayout>
  );
};

export default JobsList;





